import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronRight, BookOpen, Check, Search } from 'lucide-react';
import { AdaptiveModal } from './AdaptiveModal';
import { projectsApi } from '../../api/projects';
import { storiesApi } from '../../api/stories';
import './StoryPicker.css';

/**
 * StoryPicker — a shared explicit story-selection surface (#352 item 7),
 * relocated to Common/ in C5 (used by both the Story Board and, until C6, the
 * Corridor). Replaces any silent projects[0]/stories[0] fallback: a screen that
 * persists writer-authored rows must never guess its target.
 *
 * Two modes, chosen by the caller (table-redesign follow-up — project
 * scoping fix):
 *  - SCOPED (`scopedProject` provided): a flat list of that project's own
 *    stories only — no cross-project listing. The caller owns the fetch
 *    (`scopedStories`/`scopedLoading`) because it also needs the count
 *    BEFORE deciding whether to render a picker at all (an exactly-one-story
 *    project skips the picker entirely and navigates straight to the board —
 *    see StoryBoardPage). This mode exists so a picker opened from inside an
 *    active project never lists stories that aren't reachable from there.
 *  - GLOBAL (default, no `scopedProject`): the original all-projects →
 *    their-stories tree, for deep links with no project context at all
 *    (nothing else to scope to). Adds a filter input (filters project names
 *    — story names stay lazy-loaded per project, so filtering into them
 *    would mean abandoning that laziness) and full-text-wrap story names
 *    (previously truncated with an ellipsis).
 */
export default function StoryPicker({
  isOpen, onClose, onPick, currentStoryId, title = 'Open a story',
  scopedProject = null, scopedStories = null, scopedLoading = false,
}) {
  const [projects, setProjects] = useState([]);
  const [projectsState, setProjectsState] = useState({ loading: false, error: null, loaded: false });
  const [expanded, setExpanded] = useState({});          // projectId -> bool
  const [storiesByProject, setStoriesByProject] = useState({}); // projectId -> { loading, error, stories }
  const [filterText, setFilterText] = useState('');

  // Load projects once, the first time the picker opens. The guard is a ref, NOT
  // projectsState: depending on the `loading`/`loaded` this effect sets put it in
  // its own dependency array, so setting loading:true retriggered the effect —
  // whose cleanup flipped `cancelled` and discarded the in-flight result, hanging
  // the picker on "Loading…" forever. A ref keeps deps to [isOpen], so the effect
  // can't cancel itself. Reset on failure so a later reopen retries.
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (scopedProject) return; // scoped mode never lists other projects
    if (!isOpen || fetchedRef.current) return;
    fetchedRef.current = true;
    setProjectsState({ loading: true, error: null, loaded: false });
    projectsApi.list()
      .then((list) => {
        setProjects(Array.isArray(list) ? list : []);
        setProjectsState({ loading: false, error: null, loaded: true });
      })
      .catch((err) => {
        fetchedRef.current = false;
        setProjectsState({ loading: false, error: err.message || 'Failed to load projects', loaded: false });
      });
  }, [isOpen, scopedProject]);

  const loadStories = useCallback((projectId) => {
    setStoriesByProject((prev) => {
      if (prev[projectId]?.loading || prev[projectId]?.stories) return prev;
      return { ...prev, [projectId]: { loading: true, error: null, stories: null } };
    });
    storiesApi.list(projectId)
      .then((list) => setStoriesByProject((prev) => ({
        ...prev, [projectId]: { loading: false, error: null, stories: Array.isArray(list) ? list : [] },
      })))
      .catch((err) => setStoriesByProject((prev) => ({
        ...prev, [projectId]: { loading: false, error: err.message || 'Failed to load stories', stories: null },
      })));
  }, []);

  const toggleProject = useCallback((project) => {
    setExpanded((prev) => {
      const next = { ...prev, [project.id]: !prev[project.id] };
      return next;
    });
    if (!storiesByProject[project.id]) loadStories(project.id);
  }, [storiesByProject, loadStories]);

  // Auto-expand the first project on load so a story is one click away.
  useEffect(() => {
    if (scopedProject) return;
    if (!projectsState.loaded || projects.length === 0) return;
    const first = projects[0];
    setExpanded((prev) => (Object.keys(prev).length ? prev : { [first.id]: true }));
    if (!storiesByProject[first.id]) loadStories(first.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsState.loaded, projects, scopedProject]);

  const filteredProjects = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [projects, filterText]);

  const { loading, error, loaded } = projectsState;

  const renderStoryButton = (story, project) => {
    const selected = story.id === currentStoryId;
    return (
      <button
        key={story.id}
        className={`cor-picker__story${selected ? ' is-selected' : ''}`}
        onClick={() => onPick(project, story)}
      >
        <BookOpen size={14} />
        <span className="cor-picker__sname">{story.name || 'Untitled story'}</span>
        {selected && <Check size={14} className="cor-picker__scheck" />}
      </button>
    );
  };

  if (scopedProject) {
    return (
      <AdaptiveModal isOpen={isOpen} onClose={onClose} title={title}>
        <div className="cor-picker cor-picker--scoped">
          {scopedLoading && <p className="cor-picker__state">Loading stories…</p>}
          {!scopedLoading && scopedStories && scopedStories.length === 0 && (
            <p className="cor-picker__state">No stories in this project yet.</p>
          )}
          {!scopedLoading && scopedStories && scopedStories.length > 0 && (
            <div className="cor-picker__stories cor-picker__stories--flat">
              {scopedStories.map((story) => renderStoryButton(story, scopedProject))}
            </div>
          )}
        </div>
      </AdaptiveModal>
    );
  }

  return (
    <AdaptiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
    >
      <div className="cor-picker">
        {loaded && projects.length > 0 && (
          <div className="cor-picker__filter">
            <Search size={14} className="cor-picker__filter-icon" aria-hidden="true" />
            <input
              type="text"
              className="cor-picker__filter-input"
              placeholder="Filter projects…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              aria-label="Filter projects"
            />
          </div>
        )}

        {loading && <p className="cor-picker__state">Loading your projects…</p>}
        {error && <p className="cor-picker__state cor-picker__state--error">{error}</p>}
        {loaded && projects.length === 0 && (
          <p className="cor-picker__state">No projects yet. Create a project and a story first.</p>
        )}
        {loaded && projects.length > 0 && filteredProjects.length === 0 && (
          <p className="cor-picker__state">No projects named "{filterText}" — story names aren't searched here.</p>
        )}

        {filteredProjects.map((project) => {
          const isOpenP = !!expanded[project.id];
          const s = storiesByProject[project.id];
          return (
            <div key={project.id} className="cor-picker__project">
              <button
                className="cor-picker__phead"
                onClick={() => toggleProject(project)}
                aria-expanded={isOpenP}
              >
                {isOpenP ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                <span className="cor-picker__pname">{project.name || 'Untitled project'}</span>
                {typeof project.story_count === 'number' && (
                  <span className="cor-picker__count">
                    {project.story_count} stor{project.story_count === 1 ? 'y' : 'ies'}
                  </span>
                )}
              </button>

              {isOpenP && (
                <div className="cor-picker__stories">
                  {s?.loading && <p className="cor-picker__state">Loading stories…</p>}
                  {s?.error && <p className="cor-picker__state cor-picker__state--error">{s.error}</p>}
                  {s?.stories && s.stories.length === 0 && (
                    <p className="cor-picker__state">No stories in this project yet.</p>
                  )}
                  {s?.stories && s.stories.map((story) => renderStoryButton(story, project))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AdaptiveModal>
  );
}
